import { Body, Button, Heading } from '@assembly-js/design-system';
import { Popper } from '@mui/material';
import type { PopperPlacementType } from '@mui/material';
import React from 'react';

const POPPER_OFFSET = 8;
const DEFAULT_WIDTH = 320;

export const COACHMARK_HIGHLIGHT_CLASS = 'bg-[rgba(180,191,228,0.23)]';

/** A real element or a Popper virtual element (rect-only) anchor. */
export type CoachmarkAnchor =
  | HTMLElement
  | { getBoundingClientRect: () => DOMRect }
  | null;

// A rotated square straddling the card edge (protruding toward the anchor); the
// two outward faces carry the border so the tip matches the card outline. For
// top/bottom the arrow sits near the card's leading edge (over the target's
// start); for left/right it's vertically centered — so pair left/right with a
// centered placement ('left'/'right') and top/bottom with a '-start' placement.
const getArrowClassName = (placement: PopperPlacementType): string => {
  const base =
    'absolute h-3 w-3 rotate-45 border-0 border-solid border-gray-200 bg-white';
  if (placement.startsWith('bottom')) {
    return `${base} -top-1.5 left-6 border-l border-t`;
  }
  if (placement.startsWith('top')) {
    return `${base} -bottom-1.5 left-6 border-b border-r`;
  }
  if (placement.startsWith('left')) {
    return `${base} -right-1.5 top-1/2 -translate-y-1/2 border-r border-t`;
  }
  return `${base} -left-1.5 top-1/2 -translate-y-1/2 border-b border-l`;
};

export interface CoachmarkProps {
  open: boolean;
  anchorEl: CoachmarkAnchor;
  placement: PopperPlacementType;
  title: string;
  description: string;
  stepLabel: string;
  primaryLabel?: string;
  onPrimary?: () => void;

  lockPlacement?: boolean;

  offset?: number;

  width?: number;
}

export const Coachmark: React.FC<CoachmarkProps> = ({
  open,
  anchorEl,
  placement,
  title,
  description,
  stepLabel,
  primaryLabel,
  onPrimary,
  lockPlacement = false,
  offset = POPPER_OFFSET,
  width = DEFAULT_WIDTH,
}) => {
  if (!open || !anchorEl) {
    return null;
  }

  const modifiers = [
    { name: 'offset', options: { offset: [0, offset] } },
    ...(lockPlacement
      ? []
      : [
          { name: 'preventOverflow', options: { padding: 8 } },
          { name: 'flip', options: { padding: 8 } },
        ]),
  ];

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement={placement}
      className="z-[1301]"
      modifiers={modifiers}
    >
      {({ placement: activePlacement }) => (
        <div
          role="dialog"
          aria-label={title}
          className="pointer-events-auto relative rounded-lg border border-solid border-gray-200 bg-white p-4 shadow-[0_6px_20px_rgba(0,0,0,0.07)]"
          style={{ width }}
        >
          <span className={getArrowClassName(activePlacement)} />
          <Heading tag="h3" className="text-black-heading">
            {title}
          </Heading>
          <Body tag="div" size="sm" className="mt-1 text-gray-small">
            {description}
          </Body>
          <div className="mt-3 flex items-center justify-between">
            <Body tag="span" size="xs" className="text-gray-small">
              {stepLabel}
            </Body>
            {primaryLabel && onPrimary && (
              <Button
                variant="secondary"
                size="sm"
                label={primaryLabel}
                onClick={onPrimary}
              />
            )}
          </div>
        </div>
      )}
    </Popper>
  );
};
